import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import * as React from 'react';
import {ORDER_SHOP_HOST} from 'utils/whatsappShareUrl';
import { createHubWhatsappTemplate } from '../../api/customer/hub-templates.api';
import {
    buildWaPatternFromCreateDraft,
    sanitizeHubTemplateName,
    uniqueHubTemplateName,
    waPositionalIndexes,
} from '../../utils/whatsappPatterns';
import type { HubTemplateButtonKind, HubTemplateCreateDraft } from '../../utils/whatsappPatterns';

type PurposeId = 'document' | 'image' | 'payment' | 'order' | 'order2' | 'reminder' | 'holiday' | 'custom';

type PurposePreset = {
    id: PurposeId;
    title: string;
    hint: string;
    draft: Omit<FormState, 'customButtonText' | 'customButtonUrl' | 'footerText'> & {
        customButtonText?: string;
        customButtonUrl?: string;
        footerText?: string;
    };
};

type FormState = {
    name: string;
    category: 'UTILITY' | 'MARKETING';
    bodyText: string;
    headerFormat: 'NONE' | 'DOCUMENT' | 'IMAGE';
    buttonKind: HubTemplateButtonKind;
    customButtonText: string;
    customButtonUrl: string;
    footerText: string;
    varRoles: string[];
    examples: string[];
};

const VAR_CHIPS: { label: string; role: string; example: string }[] = [
    { label: 'שם לקוח', role: '#name', example: 'לקוח' },
    { label: 'תאריך', role: '#date', example: '03/09/2026' },
    { label: 'סוג מסמך', role: '#doctype_he', example: 'תעודת משלוח' },
    { label: 'מספר מסמך', role: '#docid', example: '1234' },
    { label: 'סכום / משתנה', role: '#var', example: '100' },
];

const ROLE_LABEL: Record<string, string> = {
    '#name': 'שם לקוח',
    '#date': 'תאריך',
    '#doctype_he': 'סוג מסמך',
    '#docid': 'מספר מסמך',
    '#var': 'סכום / משתנה',
};

const PURPOSES: PurposePreset[] = [
    {
        id: 'document',
        title: 'שליחת מסמך',
        hint: 'PDF מצורף + שם, סוג ומספר מסמך',
        draft: {
            name: 'document_send',
            category: 'UTILITY',
            headerFormat: 'DOCUMENT',
            buttonKind: 'none',
            bodyText: 'היי {{1}}\nמצורפת {{2}} מס {{3}}  עבור הזמנתך\nתודה ויום טוב',
            varRoles: ['#name', '#doctype_he', '#docid'],
            examples: ['לקוח', 'תעודה', '1234'],
        },
    },
    {
        id: 'image',
        title: 'שליחת תמונה',
        hint: 'תמונה מצורפת + שם לקוח — כל תמונה בשליחה',
        draft: {
            name: 'image_send',
            category: 'UTILITY',
            headerFormat: 'IMAGE',
            buttonKind: 'none',
            bodyText: 'היי {{1}}\nמצורפת תמונה',
            varRoles: ['#name'],
            examples: ['לקוח'],
        },
    },
    {
        id: 'payment',
        title: 'מסמך + תשלום',
        hint: 'PDF וכפתור לתשלום בלינק',
        draft: {
            name: 'doc_with_payment',
            category: 'UTILITY',
            headerFormat: 'DOCUMENT',
            buttonKind: 'pay',
            bodyText: 'היי {{1}}\nמצורפת {{2}} מס {{3}}\nלתשלום החשבון לחצו על הכפתור',
            varRoles: ['#name', '#doctype_he', '#docid'],
            examples: ['לקוח', 'תעודת משלוח', '22419'],
        },
    },
    {
        id: 'order',
        title: 'קישור הזמנה',
        hint: 'הודעה קבועה עם כפתור לחנות',
        draft: {
            name: 'order_managment',
            category: 'UTILITY',
            headerFormat: 'NONE',
            buttonKind: 'shop',
            bodyText: 'לקוח יקר!\nבאם יש לך הזמנה למחר, נא לעדכן את זה עכשיו במערכת ההזמנות, או בקישור המצו"ב',
            varRoles: [],
            examples: [],
        },
    },
    {
        id: 'order2',
        title: 'תזכורת הזמנה עם תאריך',
        hint: 'שם + תאריך + כפתור לחנות',
        draft: {
            name: 'order_managment2',
            category: 'UTILITY',
            headerFormat: 'NONE',
            buttonKind: 'shop',
            bodyText: 'היי {{1}}\nיש לך הזמנה פעילה לתאריך {{2}}\nבאם ברצונך לעדכן, לחצו על הכפתור',
            varRoles: ['#name', '#date'],
            examples: ['לקוח', '03/09/2026'],
        },
    },
    {
        id: 'reminder',
        title: 'תזכורת',
        hint: 'שם לקוח + כפתור לחנות',
        draft: {
            name: 'reminder',
            category: 'UTILITY',
            headerFormat: 'NONE',
            buttonKind: 'shop',
            bodyText: 'היי {{1}}\nתזכורת.\nנא לעדכן את ההזמנה.',
            varRoles: ['#name'],
            examples: ['לקוח'],
        },
    },
    {
        id: 'holiday',
        title: 'הודעת חג / שיווק',
        hint: 'קטגוריית שיווק — דורשת אישור מטא',
        draft: {
            name: 'holiday',
            category: 'MARKETING',
            headerFormat: 'NONE',
            buttonKind: 'shop',
            bodyText: 'היי {{1}}\nההזמנות לתאריך {{2}} פתוחות במערכת.\nיש למלא את הלינק שנשלח אליכם.',
            varRoles: ['#name', '#date'],
            examples: ['לקוח', '03/09/2026'],
        },
    },
    {
        id: 'custom',
        title: 'מותאם אישית',
        hint: 'כתבו את הטקסט בעצמכם, ואנחנו נעזור עם המשתנים',
        draft: {
            name: 'custom_message',
            category: 'UTILITY',
            headerFormat: 'NONE',
            buttonKind: 'none',
            bodyText: 'היי {{1}}\n',
            varRoles: ['#name'],
            examples: ['לקוח'],
        },
    },
];

function emptyForm(): FormState {
    return {
        name: '',
        category: 'UTILITY',
        bodyText: '',
        headerFormat: 'NONE',
        buttonKind: 'none',
        customButtonText: 'קישור',
        customButtonUrl: 'https://',
        footerText: '',
        varRoles: [],
        examples: [],
    };
}

function applyPreset(preset: PurposePreset, existingNames: string[]): FormState {
    return {
        name: uniqueHubTemplateName(preset.draft.name, existingNames),
        category: preset.draft.category,
        bodyText: preset.draft.bodyText,
        headerFormat: preset.draft.headerFormat,
        buttonKind: preset.draft.buttonKind,
        customButtonText: preset.draft.customButtonText || 'קישור',
        customButtonUrl: preset.draft.customButtonUrl || 'https://',
        footerText: preset.draft.footerText || '',
        varRoles: [...(preset.draft.varRoles || [])],
        examples: [...(preset.draft.examples || [])],
    };
}

function syncVars(bodyText: string, roles: string[], examples: string[]): { varRoles: string[]; examples: string[] } {
    const indexes = waPositionalIndexes(bodyText);
    return {
        varRoles: indexes.map((index) => roles[index - 1] || '#name'),
        examples: indexes.map((index) => examples[index - 1] || (index === 1 ? 'לקוח' : `ערך ${index}`)),
    };
}

function toCreateDraft(form: FormState): HubTemplateCreateDraft {
    return {
        name: form.name,
        language: 'he',
        category: form.category,
        bodyText: form.bodyText,
        headerFormat: form.headerFormat,
        buttonKind: form.buttonKind,
        customButtonUrl: form.customButtonUrl,
        varRoles: form.varRoles,
    };
}

function buttonsForApi(form: FormState) {
    if (form.buttonKind === 'shop') {
        return [{
            type: 'URL',
            text: 'ניהול הזמנה',
            url: `${ORDER_SHOP_HOST}/?pass={{1}}`,
            example: `${ORDER_SHOP_HOST}/?pass=sample_token`,
        }];
    }
    if (form.buttonKind === 'pay') {
        return [{
            type: 'URL',
            text: 'לתשלום החשבון',
            url: 'https://nihulkav.online/pay/s/{{1}}',
            example: 'https://nihulkav.online/pay/s/0123456789abcdef0123456789abcdef',
        }];
    }
    if (form.buttonKind === 'custom') {
        const url = form.customButtonUrl.trim();
        if (!url.toLowerCase().startsWith('https://')) return [];
        return [{
            type: 'URL',
            text: (form.customButtonText || 'קישור').slice(0, 40),
            url,
            example: url.includes('{{1}}') ? url.replace('{{1}}', 'sample') : '',
        }];
    }
    return [];
}

type Props = {
    open: boolean;
    onClose: () => void;
    channelAccountId: string;
    existingNames: string[];
    onCreated: (pattern: string) => void;
};

export default function HubCreateTemplateDialog({
    open,
    onClose,
    channelAccountId,
    existingNames,
    onCreated,
}: Props) {
    const [step, setStep] = React.useState<0 | 1>(0);
    const [purposeId, setPurposeId] = React.useState<PurposeId | null>(null);
    const [form, setForm] = React.useState<FormState>(emptyForm);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

    React.useEffect(() => {
        if (!open) return;
        setStep(0);
        setPurposeId(null);
        setForm(emptyForm());
        setBusy(false);
        setError(null);
    }, [open]);

    const patch = (partial: Partial<FormState>) => setForm((current) => ({ ...current, ...partial }));

    const choosePurpose = (preset: PurposePreset) => {
        setPurposeId(preset.id);
        setForm(applyPreset(preset, existingNames));
        setError(null);
        setStep(1);
    };

    const insertVar = (chip: typeof VAR_CHIPS[number]) => {
        const indexes = waPositionalIndexes(form.bodyText);
        const next = (indexes.length ? indexes.reduce((max, value) => Math.max(max, value), 0) : 0) + 1;
        const token = `{{${next}}}`;
        const el = bodyRef.current;
        let bodyText = form.bodyText;
        if (el && typeof el.selectionStart === 'number') {
            const start = el.selectionStart;
            const end = el.selectionEnd;
            bodyText = `${form.bodyText.slice(0, start)}${token}${form.bodyText.slice(end)}`;
        } else {
            bodyText = `${form.bodyText}${form.bodyText && !form.bodyText.endsWith(' ') && !form.bodyText.endsWith('\n') ? ' ' : ''}${token}`;
        }
        const roles = [...form.varRoles];
        const examples = [...form.examples];
        roles[next - 1] = chip.role;
        examples[next - 1] = chip.example;
        patch({ bodyText, ...syncVars(bodyText, roles, examples) });
    };

    const onBodyChange = (bodyText: string) => {
        patch({ bodyText, ...syncVars(bodyText, form.varRoles, form.examples) });
    };

    const patternPreview = buildWaPatternFromCreateDraft(toCreateDraft(form));
    const vars = waPositionalIndexes(form.bodyText);
    const nameOk = /^[a-z0-9_]{1,512}$/.test(sanitizeHubTemplateName(form.name));
    const bodyOk = form.bodyText.trim().length > 0 && form.bodyText.length <= 1024;
    const examplesOk = vars.every((_, i) => String(form.examples[i] || '').trim());
    const customUrlOk = form.buttonKind !== 'custom' || form.customButtonUrl.trim().toLowerCase().startsWith('https://');
    const canSubmit = nameOk && bodyOk && examplesOk && customUrlOk && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const created = await createHubWhatsappTemplate({
                channelAccountId,
                name: sanitizeHubTemplateName(form.name),
                language: 'he',
                category: form.category,
                bodyText: form.bodyText.trim(),
                bodyExamples: vars.map((_, i) => String(form.examples[i] || '').trim()),
                footerText: form.footerText.trim(),
                headerFormat: form.headerFormat,
                buttons: buttonsForApi(form),
            });
            const pattern = String(created.pattern || '').trim();
            if (!pattern) {
                throw new Error('ה-Hub לא החזיר תבנית שליחה לתיבה זו');
            }
            onCreated(pattern);
        } catch (e: any) {
            setError(e?.message || 'לא ניתן ליצור את התבנית');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { direction: 'rtl' } }}
        >
            <DialogTitle>
                {step === 0 ? 'תבנית ווטסאפ חדשה' : 'עריכת התבנית לפני שליחה למטא'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                {step === 0 ? (
                    <>
                        <Typography variant="body2" color="text.secondary">
                            בחרו מטרה. נמלא עבורכם טקסט, משתנים וכפתור — אפשר לשנות הכל במסך הבא.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {PURPOSES.map((purpose) => (
                                <Button
                                    key={purpose.id}
                                    variant={purposeId === purpose.id ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => choosePurpose(purpose)}
                                    sx={{ justifyContent: 'flex-start', textAlign: 'right', py: 1.25 }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2">{purpose.title}</Typography>
                                        <Typography variant="caption" color="inherit" sx={{ opacity: 0.8, display: 'block' }}>
                                            {purpose.hint}
                                        </Typography>
                                    </Box>
                                </Button>
                            ))}
                        </Box>
                    </>
                ) : (
                    <>
                        <Alert severity="info">
                            מטא צריכה לאשר תבנית חדשה. אחרי האישור אפשר לשלוח אותה מהתיבה הזו.
                        </Alert>
                        {error ? <Alert severity="error">{error}</Alert> : null}

                        <TextField
                            label="שם באנגלית (למטא)"
                            value={form.name}
                            onChange={(e) => patch({ name: sanitizeHubTemplateName(e.target.value) })}
                            inputProps={{ dir: 'ltr' }}
                            helperText="רק אותיות קטנות, מספרים וקו תחתון. זה השם ב־#pattern"
                            fullWidth
                            size="small"
                        />

                        <FormControl>
                            <FormLabel>קטגוריה</FormLabel>
                            <RadioGroup
                                row
                                value={form.category}
                                onChange={(e) => patch({ category: e.target.value as FormState['category'] })}
                            >
                                <FormControlLabel value="UTILITY" control={<Radio size="small" />} label="שירות" />
                                <FormControlLabel value="MARKETING" control={<Radio size="small" />} label="שיווק" />
                            </RadioGroup>
                        </FormControl>

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                לחצו כדי להוסיף משתנה לטקסט. בשליחה נחליף אותו בשם/תאריך של הלקוח.
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {VAR_CHIPS.map((chip) => (
                                    <Chip
                                        key={chip.role + chip.label}
                                        label={chip.label}
                                        variant="outlined"
                                        onClick={() => insertVar(chip)}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <TextField
                            label="תוכן ההודעה"
                            value={form.bodyText}
                            onChange={(e) => onBodyChange(e.target.value)}
                            inputRef={bodyRef}
                            multiline
                            minRows={5}
                            fullWidth
                            helperText={`${form.bodyText.length}/1024`}
                        />

                        {vars.map((index, i) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    select
                                    size="small"
                                    label={`{{${index}}} בשליחה`}
                                    value={form.varRoles[i] || '#name'}
                                    onChange={(e) => {
                                        const next = [...form.varRoles];
                                        next[i] = e.target.value;
                                        patch({ varRoles: next });
                                    }}
                                    sx={{ minWidth: 160 }}
                                >
                                    {Object.entries(ROLE_LABEL).map(([value, label]) => (
                                        <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label={`דוגמה למטא עבור {{${index}}}`}
                                    value={form.examples[i] || ''}
                                    onChange={(e) => {
                                        const next = [...form.examples];
                                        next[i] = e.target.value;
                                        patch({ examples: next });
                                    }}
                                />
                            </Box>
                        ))}

                        <FormControl>
                            <FormLabel>כותרת / קובץ</FormLabel>
                            <RadioGroup
                                row
                                value={form.headerFormat}
                                onChange={(e) => patch({ headerFormat: e.target.value as FormState['headerFormat'] })}
                            >
                                <FormControlLabel value="NONE" control={<Radio size="small" />} label="ללא" />
                                <FormControlLabel value="DOCUMENT" control={<Radio size="small" />} label="PDF מצורף" />
                                <FormControlLabel value="IMAGE" control={<Radio size="small" />} label="תמונה מצורפת" />
                            </RadioGroup>
                        </FormControl>

                        <FormControl>
                            <FormLabel>כפתור</FormLabel>
                            <RadioGroup
                                value={form.buttonKind}
                                onChange={(e) => patch({ buttonKind: e.target.value as HubTemplateButtonKind })}
                            >
                                <FormControlLabel value="none" control={<Radio size="small" />} label="ללא" />
                                <FormControlLabel value="shop" control={<Radio size="small" />} label="ניהול הזמנה (חנות)" />
                                <FormControlLabel value="pay" control={<Radio size="small" />} label="תשלום בלינק" />
                                <FormControlLabel value="custom" control={<Radio size="small" />} label="קישור מותאם" />
                            </RadioGroup>
                        </FormControl>

                        {form.buttonKind === 'custom' ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    label="טקסט הכפתור"
                                    value={form.customButtonText}
                                    onChange={(e) => patch({ customButtonText: e.target.value })}
                                />
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="כתובת https"
                                    value={form.customButtonUrl}
                                    onChange={(e) => patch({ customButtonUrl: e.target.value })}
                                    inputProps={{ dir: 'ltr' }}
                                    helperText="לסיומת דינמית הוסיפו {{1}} בסוף הכתובת"
                                />
                            </Box>
                        ) : null}

                        <TextField
                            size="small"
                            label="כותרת תחתונה (אופציונלי)"
                            value={form.footerText}
                            onChange={(e) => patch({ footerText: e.target.value.slice(0, 60) })}
                            helperText={`${form.footerText.length}/60`}
                        />

                        <Box sx={{
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}>
                            <Typography variant="caption" color="text.secondary">כך זה ייכתב בתיבת ההודעה</Typography>
                            <Typography
                                component="pre"
                                dir="ltr"
                                sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', fontSize: 12, textAlign: 'left' }}
                            >
                                {patternPreview || '—'}
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                {step === 1 ? (
                    <Button onClick={() => { setStep(0); setError(null); }} disabled={busy}>
                        חזרה
                    </Button>
                ) : null}
                <Button onClick={onClose} disabled={busy}>ביטול</Button>
                {step === 1 ? (
                    <LoadingButton
                        variant="contained"
                        color="success"
                        loading={busy}
                        disabled={!canSubmit}
                        onClick={() => void submit()}
                    >
                        צור תבנית והכנס לתיבה
                    </LoadingButton>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
